import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SitePage, { SectionHeader, STUDIO_EMAIL } from './SiteChrome';
import { getCatalog, getDays, getAvailability, createBooking } from './bookingClient';

// Contact (spec §8) + the scheduling kit: the inquiry form, an interests
// preselect (the five pillars, seeded server-side), and an optional meeting
// picker — choose a day and a time with Anastasia, or skip and just send the
// note. Validation is inline and in plain words (spec §10). The backend
// re-validates everything and books the slot race-free (bookingApi.js).

const fmtDate = (iso) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

function Calendar({ month, days, selected, onPick, onMonth, canPrev, canNext }) {
  const [y, m] = month.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const blanks = first.getDay();
  const count = new Date(y, m, 0).getDate();
  const title = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  return (
    <div className="ac-cal">
      <div className="ac-cal-head">
        <button type="button" className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => onMonth(-1)} disabled={!canPrev} aria-label="Previous month">←</button>
        <span className="ac-cal-title">{title}</span>
        <button type="button" className="ac-btn ac-btn-ghost ac-btn-sm" onClick={() => onMonth(1)} disabled={!canNext} aria-label="Next month">→</button>
      </div>
      <div className="ac-cal-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => <span className="ac-cal-dow" key={i}>{d}</span>)}
        {Array.from({ length: blanks }).map((_, i) => <span key={`b${i}`} />)}
        {Array.from({ length: count }).map((_, i) => {
          const date = `${month}-${String(i + 1).padStart(2, '0')}`;
          const open = days ? (days[date] || 0) > 0 : false;
          return (
            <button
              type="button"
              key={date}
              className={`ac-cal-day ${selected === date ? 'on' : ''}`}
              disabled={!open}
              onClick={() => onPick(date)}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Contact() {
  const [params] = useSearchParams();
  const [catalog, setCatalog] = useState(null);
  const [loadError, setLoadError] = useState('');

  // Interests: preselected via /contact?interest=web (from Services links).
  const [interests, setInterests] = useState(() =>
    params.get('interest') ? [params.get('interest')] : []
  );

  const [form, setForm] = useState({
    name: '', company: '', site: '', message: '', budget: '', timeline: '', email: '',
  });
  const [errors, setErrors] = useState({});

  // Meeting picker (optional)
  const [wantMeeting, setWantMeeting] = useState(true);
  const [month, setMonth] = useState('');
  const [days, setDays] = useState(null);
  const [date, setDate] = useState('');
  const [avail, setAvail] = useState(null);
  const [startMin, setStartMin] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    getCatalog()
      .then((c) => { setCatalog(c); setMonth(c.window.today.slice(0, 7)); })
      .catch((e) => setLoadError(e.message));
  }, []);

  useEffect(() => {
    if (!catalog || !month || !wantMeeting) return;
    setDays(null);
    getDays(month).then((r) => setDays(r.days)).catch(() => setDays({}));
  }, [catalog, month, wantMeeting]);

  useEffect(() => {
    if (!date) return;
    setAvail(null);
    setStartMin(null);
    getAvailability(date).then(setAvail).catch(() => setAvail({ slots: [] }));
  }, [date]);

  const monthNav = useCallback((dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setDate('');
  }, [month]);

  const toggleInterest = (id) =>
    setInterests((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  // Inline validation in plain words (spec §10).
  const validate = useMemo(() => () => {
    const e = {};
    if (!form.name.trim()) e.name = 'We need a name.';
    if (!form.company.trim()) e.company = 'We need your company.';
    if (!/.+@.+\..+/.test(form.email.trim())) e.email = 'That email doesn\u2019t look right.';
    if (form.site.trim() && !/^(https?:\/\/)?[^\s.]+\.[^\s]{2,}$/i.test(form.site.trim())) {
      e.site = 'That URL doesn\u2019t look right.';
    }
    if (!form.message.trim()) e.message = 'Tell us a little about what you need.';
    if (wantMeeting && (!date || startMin === null)) {
      e.slot = 'Pick a day and a time — or choose "just reply by email."';
    }
    return e;
  }, [form, wantMeeting, date, startMin]);

  async function submit(ev) {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await createBooking({
        ...form,
        site: form.site.trim() || undefined,
        budget: form.budget || undefined,
        timeline: form.timeline || undefined,
        interests,
        ...(wantMeeting ? { date, startMin } : {}),
      });
      setConfirmation(res);
    } catch (err) {
      setSubmitError(err.message);
      // Slot races: clear the picked time so the user re-picks.
      if (/slot/.test(err.message)) { setDate(''); setAvail(null); setStartMin(null); setDays(null); getDays(month).then((r) => setDays(r.days)).catch(() => {}); }
    } finally {
      setSubmitting(false);
    }
  }

  const field = (key, label, props = {}, El = 'input') => (
    <div className="ac-field">
      <label htmlFor={`ac-${key}`}>{label}</label>
      <El
        id={`ac-${key}`}
        className={`${El === 'textarea' ? 'ac-textarea' : 'ac-input'} ${errors[key] ? 'ac-field-error' : ''}`}
        value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        {...props}
      />
      {errors[key] && <span className="ac-error" role="alert">{errors[key]}</span>}
    </div>
  );

  const studioEmail = catalog?.studioEmail || STUDIO_EMAIL;

  return (
    <SitePage cta={false}>
      <section className="ac-section">
        <div className="ac-wrap">
          {confirmation ? (
            /* Success state (spec §10) — replaces the form. */
            <div className="ac-confirm ac-rise">
              <span className="ac-kicker">{confirmation.date ? 'You\u2019re on the calendar' : 'Sent'}</span>
              {confirmation.date ? (
                <>
                  <div className="ac-confirm-code ac-rise" style={{ '--d': '0.1s' }}>{confirmation.code}</div>
                  <hr className="ac-wave" aria-hidden="true" style={{ margin: 0 }} />
                  <h2 className="ac-rise" style={{ '--d': '0.2s' }}>{fmtDate(confirmation.date)} at {confirmation.time}.</h2>
                  <p className="ac-muted">
                    {confirmation.durationMin} minutes with Anastasia to talk through what
                    you’re building. We’ll confirm by email. Save your code.
                  </p>
                </>
              ) : (
                <>
                  <h2 className="ac-rise">Got it.</h2>
                  <hr className="ac-wave" aria-hidden="true" style={{ margin: 0 }} />
                  <p className="ac-muted ac-rise" style={{ '--d': '0.15s' }}>We read every inquiry and reply within two business days.</p>
                </>
              )}
              <p className="ac-muted ac-small">or write to <a href={`mailto:${studioEmail}`}>{studioEmail}</a></p>
            </div>
          ) : (
            <>
              <SectionHeader kicker="Contact" title="Start a project." />
              <p style={{ marginBottom: 40 }}>
                Tell us what you’re building and where it lives today. We reply
                within two business days.
              </p>

              <form className="ac-form" onSubmit={submit} noValidate>
                {/* Interests preselect — the five pillars from the catalog. */}
                <div className="ac-field">
                  <label>What are you interested in?</label>
                  {loadError ? (
                    <span className="ac-muted ac-small">
                      The interest list didn’t load — no matter, tell us below.
                    </span>
                  ) : !catalog ? (
                    <span className="ac-muted ac-small">Loading…</span>
                  ) : (
                    <div className="ac-chips">
                      {catalog.interests.map((s) => (
                        <button
                          type="button"
                          key={s.id}
                          className={`ac-chip ${interests.includes(s.id) ? 'on' : ''}`}
                          aria-pressed={interests.includes(s.id)}
                          onClick={() => toggleInterest(s.id)}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="ac-form-row">
                  {field('name', 'Name', { autoComplete: 'name', required: true })}
                  {field('company', 'Company', { autoComplete: 'organization', required: true })}
                </div>
                <div className="ac-form-row">
                  {field('email', 'Email', { type: 'email', autoComplete: 'email', required: true })}
                  {field('site', 'Current site, if any', { type: 'url', inputMode: 'url', placeholder: 'https://…' })}
                </div>
                {field('message', 'What do you need?', {
                  placeholder: 'Identity, site, photography, presence — or all of it.',
                  required: true,
                }, 'textarea')}
                <div className="ac-form-row">
                  <div className="ac-field">
                    <label htmlFor="ac-budget">Budget range</label>
                    <select
                      id="ac-budget"
                      className="ac-select"
                      value={form.budget}
                      onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))}
                    >
                      <option value="">Choose…</option>
                      {(catalog?.budgets || []).map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="ac-field">
                    <label htmlFor="ac-timeline">Timeline</label>
                    <select
                      id="ac-timeline"
                      className="ac-select"
                      value={form.timeline}
                      onChange={(e) => setForm((f) => ({ ...f, timeline: e.target.value }))}
                    >
                      <option value="">Choose…</option>
                      {(catalog?.timelines || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                {/* Meeting picker — book a time with Anastasia, or skip. */}
                <div className="ac-field" style={{ marginTop: 10 }}>
                  <label>Talk it through?</label>
                  <div className="ac-chips">
                    <button type="button" className={`ac-chip ${wantMeeting ? 'on' : ''}`} aria-pressed={wantMeeting} onClick={() => setWantMeeting(true)}>
                      Book {catalog?.meetingDurationMin || 30} minutes with Anastasia
                    </button>
                    <button type="button" className={`ac-chip ${!wantMeeting ? 'on' : ''}`} aria-pressed={!wantMeeting} onClick={() => { setWantMeeting(false); setDate(''); setStartMin(null); }}>
                      Just reply by email
                    </button>
                  </div>
                </div>

                {wantMeeting && catalog && (
                  <div className="ac-rise" style={{ display: 'grid', gap: 20 }}>
                    <Calendar
                      month={month}
                      days={days}
                      selected={date}
                      onPick={setDate}
                      onMonth={monthNav}
                      canPrev={month > catalog.window.today.slice(0, 7)}
                      canNext={month < catalog.window.maxDate.slice(0, 7)}
                    />
                    {date && (
                      <div style={{ display: 'grid', gap: 12 }}>
                        <h3 style={{ fontSize: '1.05rem' }}>{fmtDate(date)}</h3>
                        {!avail ? (
                          <span className="ac-muted ac-small">Checking the day…</span>
                        ) : avail.slots.length === 0 ? (
                          <span className="ac-muted ac-small">That day just filled up — pick another.</span>
                        ) : (
                          <div className="ac-slots">
                            {avail.slots.map((s, i) => (
                              <button type="button" key={s.startMin} style={{ '--d': `${i * 0.045}s` }} className={`ac-slot ${startMin === s.startMin ? 'on' : ''}`} onClick={() => setStartMin(s.startMin)}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {errors.slot && <span className="ac-error" role="alert">{errors.slot}</span>}
                  </div>
                )}

                {submitError && (
                  /* Error state (spec §10) */
                  <p className="ac-error" role="alert">
                    {/slot/.test(submitError)
                      ? submitError
                      : <>That didn’t send. Try once more, or write to <a href={`mailto:${studioEmail}`}>{studioEmail}</a>.</>}
                  </p>
                )}

                <div>
                  <button className="ac-btn" type="submit" disabled={submitting}>
                    {submitting ? 'Sending…' : wantMeeting ? 'Send & book the time' : 'Send inquiry'}
                  </button>
                </div>
                <p className="ac-muted ac-small">or write to <a href={`mailto:${studioEmail}`}>{studioEmail}</a></p>
              </form>
            </>
          )}
        </div>
      </section>
    </SitePage>
  );
}
